import React, { createContext, useContext, useReducer, useEffect } from 'react';
import { authService } from '../services/authService';

// Estado inicial
const initialState = {
  user: null,
  isAuthenticated: false,
  loading: true,
  error: null,
};

// Actions
const AuthActions = {
  SET_LOADING: 'SET_LOADING',
  SET_AUTH: 'SET_AUTH',
  SET_ERROR: 'SET_ERROR',
  LOGOUT: 'LOGOUT',
};

// Reducer
const authReducer = (state, action) => {
  switch (action.type) {
    case AuthActions.SET_LOADING:
      return {
        ...state,
        loading: action.payload,
      };
    case AuthActions.SET_AUTH:
      return {
        ...state,
        user: action.payload.user,
        isAuthenticated: true,
        loading: false,
        error: null,
      };
    case AuthActions.SET_ERROR:
      return {
        ...state,
        error: action.payload,
        loading: false,
      };
    case AuthActions.LOGOUT:
      return {
        ...initialState,
        loading: false,
      };
    default:
      return state;
  }
};

// Context
const AuthContext = createContext();

// Provider
export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // Verificar autenticación al cargar
  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      if (authService.isAuthenticated()) {
        const data = await authService.verify();
        if (data.success) {
          dispatch({
            type: AuthActions.SET_AUTH,
            payload: { user: data.user },
          });
        } else {
          await authService.logout();
          dispatch({ type: AuthActions.LOGOUT });
        }
      } else {
        dispatch({ type: AuthActions.SET_LOADING, payload: false });
      }
    } catch (error) {
    // console.error('Auth check error:', error);
      await authService.logout();
      dispatch({ type: AuthActions.LOGOUT });
    }
  };

  const login = async (credentials) => {
    try {
      dispatch({ type: AuthActions.SET_LOADING, payload: true });
      const data = await authService.login(credentials);

      if (data.success) {
        dispatch({ type: AuthActions.SET_AUTH, payload: { user: data.user } });
        return { success: true };
      } else {
        dispatch({ type: AuthActions.SET_ERROR, payload: data.error || 'Error en el login' });
        return { success: false, error: data.error };
      }
    } catch (error) {
      dispatch({ type: AuthActions.SET_ERROR, payload: error.message });
      return {
        success: false,
        error: error.message,
        requiresVerification: error.data?.requiresVerification || false,
        email: error.data?.email || credentials.username,
      };
    }
  };

  const register = async (userData) => {
    try {
      dispatch({ type: AuthActions.SET_LOADING, payload: true });
      const data = await authService.register(userData);
      
      dispatch({ type: AuthActions.SET_LOADING, payload: false });
      return data;
    } catch (error) {
      dispatch({
        type: AuthActions.SET_ERROR,
        payload: error.message,
      });
      return { success: false, error: error.message };
    }
  };

  const logout = async () => {
    try {
      await authService.logout();
    } catch (error) {
      //console.error('Logout error:', error);
    } finally {
      dispatch({ type: AuthActions.LOGOUT });
    }
  };

  const clearError = () => {
    dispatch({ type: AuthActions.SET_ERROR, payload: null });
  };

  const value = {
    ...state,
    login,
    register,
    logout,
    clearError,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Hook personalizado para usar el contexto
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth debe ser usado dentro de un AuthProvider');
  }
  return context;
};